import { useState } from "react";
import { CreditCard, CheckCircle2, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { useApi } from "../../hooks/useApi";
import { pay, errMsg } from "../../lib/api";
import { PageHeader, Panel, Btn, Empty, shortDate } from "../../components/app/kit";
import { BrandLine } from "../../components/Brand";

export default function Billing() {
  const { membership } = useAuth();
  const tid = membership?.team_id;
  const plan = useApi(tid && `/plan?team_id=${tid}`, pay);
  const sub = useApi(tid && `/team/${tid}`, pay);
  const [busy, setBusy] = useState(false);
  const welcome = new URLSearchParams(window.location.search).get("welcome") === "1";

  if (membership?.role !== "owner") return <Empty title="Owners only" text="Ask your team owner to manage the subscription." />;

  const s = sub.data ?? {};
  const comp = membership?.subscription_status === "comp" || s.status === "comp";
  const paid = comp || !!s.active;
  const p = plan.data ?? {};
  const trial = Number(p.trial_days) || 0;
  const price = p.amount ? `$${(p.amount / 100).toFixed(2)}` : "—";
  const label = comp ? "Free access" : s.active ? (s.status === "trialing" || s.trial ? "Active · trial" : "Active") : s.status === "canceled" ? "Canceled" : "Not subscribed";

  const go = async (path) => {
    setBusy(true);
    try {
      const { data } = await pay.post(path, { team_id: tid, origin_url: window.location.origin });
      const url = data?.checkout_url ?? data?.url;
      if (!url) throw new Error("Checkout link unavailable. Try again.");
      window.location.assign(url);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow={membership.team_name} title={welcome ? "Welcome to GAMEDAY" : "Subscription"} />
      <BrandLine className="-mt-4 mb-6" testid="billing-brand-line" />
      <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
        <Panel data-testid="billing-panel">
          <p className="font-mono-gd text-xs uppercase tracking-[0.25em] text-slate-400">Team plan</p>
          <p className="mt-2 font-display text-6xl font-black text-white">{price}<span className="ml-2 text-xl font-bold text-slate-400">/ {p.interval ?? "month"}</span></p>
          <p className="mt-2 text-sm text-slate-400">Billed to the team. One GAMEDAY Courtside plan covers the website at HoopsHQ.net for your staff and players.</p>
          {trial > 0 && !paid && (
            <p data-testid="billing-trial-note" className="mt-4 rounded-xl border border-[#39FF14]/30 bg-[#39FF14]/10 px-4 py-3 text-sm text-white">
              <span className="font-semibold text-[#39FF14]">{trial}-day free trial.</span> You won't be charged until day {trial + 1}. Cancel before then and you pay nothing.
            </p>
          )}
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <span data-testid="billing-status" className={`rounded-full px-3 py-1 font-mono-gd text-xs uppercase tracking-wider ring-1 ${paid ? "bg-[#39FF14]/15 text-[#39FF14] ring-[#39FF14]/40" : "bg-amber-400/15 text-amber-300 ring-amber-400/40"}`}>{label}</span>
            {s.active && s.since && <span className="font-mono-gd text-xs text-slate-400">since {shortDate(s.since)}</span>}
            {p.mode === "test" && <span data-testid="billing-test-mode" className="rounded-full bg-[#162032] px-3 py-1 font-mono-gd text-[11px] uppercase tracking-wider text-slate-300">Test mode</span>}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            {!paid && (
              <Btn data-testid="billing-subscribe-button" onClick={() => go("/checkout")} disabled={busy || plan.loading}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}{trial > 0 ? `Start ${trial}-day free trial` : `Subscribe · ${price}/${p.interval ?? "month"}`}
              </Btn>
            )}
            {s.active && s.has_portal && (
              <Btn variant="ghost" data-testid="billing-manage-button" onClick={() => go("/portal")} disabled={busy}>Manage billing</Btn>
            )}
          </div>
        </Panel>
        <Panel>
          {paid ? (
            <div className="space-y-3"><CheckCircle2 className="h-8 w-8 text-[#39FF14]" /><p className="font-display text-2xl font-bold uppercase text-white">You're all set</p><p className="text-sm text-slate-400">Head to the Overview to see your roster and log games in the mobile app.</p></div>
          ) : (
            <div className="space-y-3"><ShieldCheck className="h-8 w-8 text-[#39FF14]" /><p className="font-display text-2xl font-bold uppercase text-white">Secure checkout</p><p className="text-sm text-slate-400">Payment is handled by Stripe. Card details never touch GAMEDAY. Cancel anytime from Manage billing.</p></div>
          )}
        </Panel>
      </div>
    </div>
  );
}
